import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./Resources.css";
import "./HomePage.css";
import PageHeader from "./components/PageHeader.js";

export default function Campaigns() {
  const [name, setName] = useState("Zolaid Demo Business");
  const [description, setDescription] = useState("An AI website builder that helps small businesses launch fast with automation and great UX.");
  const [tone, setTone] = useState("modern");
  const [primaryColor, setPrimaryColor] = useState("#7c3aed");
  const [secondaryColor, setSecondaryColor] = useState("#2563eb");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [lastWebsiteData, setLastWebsiteData] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL || "";

  const run = async () => {
    try {
      setError("");
      setLoading(true);
      const endpoint = apiUrl ? `${apiUrl}/campaign/generate` : "/campaign/generate";
      const websiteData = {
        name,
        description,
        theme: "modern",
        techStack: "html",
        integrationMode: "demo",
        payments: { enabled: false, provider: "stripe" },
        authProviders: { google: false, apple: false, microsoft: false, facebook: false },
        standardPages: { about: true, contact: true, privacy: true, terms: true, cookie: true, refund: false },
        pages: [
          { id: "home", title: "Home", content: "Landing page for the business." },
          { id: "about", title: "About", content: "About the business." },
          { id: "contact", title: "Contact", content: "Contact page." },
        ],
        brand: { tone, primaryColor, secondaryColor, fontFamily: "system-ui" },
      };
      const resp = await axios.post(endpoint, { websiteData });
      setCampaign(resp.data?.campaign || null);
      setLastWebsiteData(websiteData);
    } catch (e) {
      console.error("campaign generate failed:", e);
      setError(e?.response?.data?.error || "Failed to generate campaign.");
      setCampaign(null);
      setLastWebsiteData(null);
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const blob = new Blob([JSON.stringify({ campaign }, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zolaid-campaign-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const heroUrl = campaign?.heroImageUrl
    ? (apiUrl && String(campaign.heroImageUrl).startsWith("/") ? `${apiUrl.replace(/\/+$/, "")}${campaign.heroImageUrl}` : campaign.heroImageUrl)
    : "";

  const saveCampaignAsProject = async ({ openAfter = false } = {}) => {
    try {
      setProjectError("");
      setProjectLoading(true);
      if (!lastWebsiteData) throw new Error("No website inputs available.");

      // Attach hero image into Home content (so WebsiteBuilder can insert it as an image section if desired).
      const pages = Array.isArray(lastWebsiteData.pages) ? lastWebsiteData.pages.map((p) => ({ ...p })) : [];
      const homeIdx = pages.findIndex((p) => String(p.id) === "home");
      const hero = heroUrl || "";
      if (homeIdx !== -1 && hero) {
        pages[homeIdx].contentHtml = `<p><img src="${hero}" alt="Hero image" style="max-width:100%;height:auto;border-radius:12px;" /></p>\n<p>${String(pages[homeIdx].content || "")}</p>`;
      }

      const websiteData = { ...lastWebsiteData, pages };
      const endpointCreate = apiUrl ? `${apiUrl}/projects/create` : "/projects/create";
      const createResp = await axios.post(endpointCreate, { name: `${websiteData.name} (Campaign Project)`, websiteData });
      const projectId = createResp.data?.project?.id;
      if (!projectId) throw new Error("Project id missing.");

      try { localStorage.setItem("zolaid_current_project_id_v1", String(projectId)); } catch (_) {}

      if (openAfter) {
        // Fetch full project then handoff to WebsiteBuilder
        const endpointGet = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
        const resp = await axios.get(endpointGet, { params: { id: projectId } });
        const project = resp.data?.project;
        if (project) {
          try { localStorage.setItem("zolaid_project_to_load_v1", JSON.stringify(project)); } catch (_) {}
          window.location.assign("/chatbot");
        }
      }
    } catch (e) {
      console.error("save campaign as project failed:", e);
      setProjectError(e?.response?.data?.error || e?.message || "Failed to save project.");
    } finally {
      setProjectLoading(false);
    }
  };

  return (
    <div className="resources-page">
      <PageHeader
        customNav={[
          { label: "Resources", to: "/Resources" },
          { label: "Templates", to: "/Templates" },
          { label: "Impact", to: "/Impact" },
          { label: "Campaigns", to: "/Campaigns" },
        ]}
      />

      <div className="resources-content">
        <h1>Campaign Pack (Prototype)</h1>
        <p className="resources-subtitle">
          Generates a website + branded hero image + promo storyboard as one bundle.
        </p>

        <section style={sectionStyle}>
          <h2>Inputs</h2>
          <div style={gridStyle}>
            <div>
              <div style={labelStyle}>Name</div>
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Tone</div>
              <select value={tone} onChange={(e) => setTone(e.target.value)} style={inputStyle}>
                <option value="modern">Modern</option>
                <option value="friendly">Friendly</option>
                <option value="premium">Premium</option>
                <option value="bold">Bold</option>
                <option value="minimal">Minimal</option>
              </select>
            </div>
            <div>
              <div style={labelStyle}>Primary color</div>
              <input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Secondary color</div>
              <input value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={labelStyle}>Description</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} style={textareaStyle} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <button className="generate-btn" type="button" disabled={loading} onClick={run}>
              {loading ? "Generating..." : "Generate campaign pack"}
            </button>
            {campaign && (
              <button className="generate-btn" type="button" onClick={download}>
                Download JSON
              </button>
            )}
          </div>
          {error && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{error}</div>}
        </section>

        {campaign && (
          <section style={sectionStyle}>
            <h2>Outputs</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="generate-btn" type="button" disabled={projectLoading} onClick={() => saveCampaignAsProject({ openAfter: false })}>
                  {projectLoading ? "Saving..." : "Save as Project"}
                </button>
                <button className="generate-btn" type="button" disabled={projectLoading} onClick={() => saveCampaignAsProject({ openAfter: true })}>
                  {projectLoading ? "Opening..." : "Save + Open in WebsiteBuilder"}
                </button>
                {projectError && <span style={{ fontSize: 12, color: "#ffb4b4" }}>{projectError}</span>}
              </div>
              <div>
                <div style={labelStyle}>Hero image</div>
                {heroUrl ? (
                  <img src={heroUrl} alt="Campaign hero" style={{ width: "100%", borderRadius: 14, border: "1px solid rgba(255,255,255,0.12)" }} />
                ) : (
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>No hero image.</div>
                )}
              </div>
              <div>
                <div style={labelStyle}>Storyboard</div>
                <pre style={preStyle}>{JSON.stringify(campaign.storyboard || {}, null, 2)}</pre>
              </div>
            </div>
          </section>
        )}
      </div>

      <footer className="resources-footer">
        <p>Â© 2025 ZOLAID. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/PrivacyPolicy">Privacy Policy</Link>
          <span>|</span>
          <Link to="/TermsOfService">Terms of Service</Link>
          <span>|</span>
          <Link to="/Contact">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

const sectionStyle = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.10)",
  background: "rgba(255,255,255,0.04)",
  marginBottom: 12,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 10,
};

const labelStyle = { fontSize: 12, fontWeight: 800, color: "#cccccc", marginBottom: 6 };

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(0,0,0,0.18)",
  color: "#ffffff",
  outline: "none",
};

const textareaStyle = inputStyle;

const preStyle = {
  margin: 0,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(0,0,0,0.22)",
  color: "#ffffff",
  overflowX: "auto",
  fontSize: 12,
};

